import { useState, useRef, useEffect, useMemo } from 'react'
import clsx from 'clsx'

import {
  DESCRIPTION_PREVIEW_MAX_CHARS,
  truncateDescriptionPreview,
} from '../../utils/descriptionPreview'

interface DescriptionPreviewProps {
  text: string | undefined | null
  /** Limite customizado de caracteres; usa o da env quando não informado */
  maxChars?: number
  /** ID estável do item, usado em data-testid/aria para testes e a11y */
  itemId: string
}

/**
 * Box de prévia da descrição da requisição na listagem principal.
 *
 * Mostra um trecho (até `maxChars` ou `VITE_DESCRIPTION_PREVIEW_MAX_CHARS`)
 * limitado a 2-3 linhas via `line-clamp`, sem alterar o posicionamento
 * dos demais elementos do card. Ao passar o cursor, exibe um tooltip
 * com o texto integral em cantos arredondados, sombra suave e z-50.
 *
 * Quando o texto é vazio ou cabe inteiro no limite, o tooltip não é
 * renderizado (não há informação extra a revelar).
 */
const TOOLTIP_CLOSE_DELAY_MS = 100

export const DescriptionPreview = ({
  text,
  maxChars = DESCRIPTION_PREVIEW_MAX_CHARS,
  itemId,
}: DescriptionPreviewProps) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const trimmed = useMemo(() => (text ?? '').trim(), [text])

  const preview = useMemo(
    () => truncateDescriptionPreview(trimmed, maxChars),
    [trimmed, maxChars],
  )

  const hasOverflow = trimmed.length > 0 && trimmed.length > maxChars

  const cancelPendingClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const scheduleClose = () => {
    cancelPendingClose()
    closeTimerRef.current = setTimeout(() => {
      setOpen(false)
    }, TOOLTIP_CLOSE_DELAY_MS)
  }

  const handleMouseEnter = () => {
    cancelPendingClose()
    if (hasOverflow) setOpen(true)
  }

  const handleMouseLeave = () => {
    scheduleClose()
  }

  useEffect(() => {
    return () => {
      cancelPendingClose()
    }
  }, [])

  // Fecha o tooltip se o utilizador clicar fora ou pressionar Escape —
  // melhora a experiência sem dependências externas.
  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  if (trimmed.length === 0) {
    return (
      <div
        data-testid={`description-preview-${itemId}`}
        className="text-xs italic text-brand-text-muted"
      >
        Sem descrição.
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      data-testid={`description-preview-${itemId}`}
      className="relative w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <p
        onFocus={() => hasOverflow && setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          if (hasOverflow) setOpen((current) => !current)
        }}
        tabIndex={hasOverflow ? 0 : -1}
        role={hasOverflow ? 'button' : undefined}
        aria-label={
          hasOverflow
            ? `Prévia da descrição: ${preview}. Carregue para ver o texto integral.`
            : `Descrição: ${trimmed}`
        }
        aria-describedby={
          hasOverflow ? `description-tooltip-${itemId}` : undefined
        }
        className={clsx(
          'm-0 min-w-0 max-w-full cursor-default text-sm leading-snug text-brand-text-secondary',
          'line-clamp-2 md:line-clamp-3',
          'break-words',
        )}
      >
        {preview}
      </p>

      {hasOverflow && open ? (
        <div
          id={`description-tooltip-${itemId}`}
          role="tooltip"
          data-testid={`description-tooltip-${itemId}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={clsx(
            'absolute left-0 bottom-full z-50 mb-2 w-full max-w-md',
            'rounded-md bg-brand-neutral-600 px-3 py-2 text-xs font-normal text-white shadow-lg',
            'break-words whitespace-pre-wrap',
          )}
        >
          {trimmed}
        </div>
      ) : null}
    </div>
  )
}

export default DescriptionPreview